<template>
  <div
    class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#1E293B] p-5 sm:p-6 shadow-[0_1px_3px_rgba(0,0,0,0.03)] flex flex-col h-full"
  >
    <!-- Header -->
    <div class="flex items-center justify-between mb-4 sm:mb-5">
      <div>
        <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-tight">
          Campus Calendar
        </h3>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Events scheduled for this month
        </p>
      </div>

      <span
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/40"
      >
        <Calendar class="w-3.5 h-3.5" />
        <span>{{ currentMonthEvents.length }} {{ currentMonthEvents.length === 1 ? 'Event' : 'Events' }}</span>
      </span>
    </div>

    <!-- Scrollable Events List -->
    <div class="flex-1 overflow-y-auto max-h-60 sm:max-h-72 custom-scrollbar pr-1 space-y-2.5">
      <template v-if="currentMonthEvents.length">
        <div
          v-for="event in currentMonthEvents"
          :key="event.id"
          class="group flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-[#1E293B] hover:border-indigo-200 dark:hover:border-indigo-900/60 bg-slate-50/60 dark:bg-[#0E1526]/50 hover:bg-white dark:hover:bg-[#18243A] transition-all duration-150"
        >
          <!-- Date Chip -->
          <div
            class="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/40 flex flex-col items-center justify-center shrink-0 shadow-2xs"
          >
            <span class="text-[9px] font-extrabold uppercase leading-none">
              {{ getEventMonthShort(event) }}
            </span>
            <span class="text-xs font-black leading-tight mt-0.5">
              {{ getEventDayNumber(event) }}
            </span>
          </div>

          <!-- Event Details Stack -->
          <div class="flex-1 min-w-0">
            <p class="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {{ event.summary || "Scheduled Institutional Event" }}
            </p>
            <p class="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-1">
              <Clock class="w-3 h-3 text-slate-400 shrink-0" />
              <span>{{ formatEventTime(event) }}</span>
            </p>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-8 text-center"
      >
        <div class="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mb-2">
          <Calendar class="w-5 h-5 opacity-60" />
        </div>
        <p class="text-xs font-semibold text-slate-700 dark:text-slate-300">
          No events scheduled
        </p>
        <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 max-w-xs">
          Your institutional calendar is clear for the remainder of this month.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from "vue";
  import { useStore } from "vuex";
  import { Calendar, Clock } from "lucide-vue-next";

  export default {
    name: "EventsThisMonth",
    components: { Calendar, Clock },
    setup() {
      const store = useStore();
      const currentDate = ref(new Date());
      const events = computed(() => store.getters.getUpcomingEvents || []);

      const calendarId = process.env.VUE_APP_GOOGLE_CALENDAR_ID;
      const apiKey = process.env.VUE_APP_GOOGLE_API_KEY;

      const fetchEventsForMonth = async () => {
        if (!currentDate.value || isNaN(currentDate.value.getTime())) {
          return;
        }

        const timeMin = new Date(
          currentDate.value.getFullYear(),
          currentDate.value.getMonth(),
          1,
        ).toISOString();

        const timeMax = new Date(
          currentDate.value.getFullYear(),
          currentDate.value.getMonth() + 1,
          0,
        ).toISOString();

        try {
          await store.dispatch("fetchUpcomingEvents", {
            calendarId,
            apiKey,
            timeMin,
            timeMax,
          });
        } catch (error) {
          // Keep resilient
        }
      };

      const formatEventTime = (event) => {
        const start = event.start?.dateTime || event.start?.date;
        if (!start) return "All Day";
        const date = new Date(start);
        return date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      };

      const getEventMonthShort = (event) => {
        const start = event.start?.dateTime || event.start?.date;
        if (!start) return "NOW";
        return new Date(start).toLocaleDateString("en-US", { month: "short" });
      };

      const getEventDayNumber = (event) => {
        const start = event.start?.dateTime || event.start?.date;
        if (!start) return "--";
        return new Date(start).getDate();
      };

      const currentMonthEvents = computed(() => {
        return events.value
          .filter((event) => {
            const start = event.start?.dateTime || event.start?.date;
            if (!start) return false;
            const date = new Date(start);
            return (
              date.getMonth() === currentDate.value.getMonth() &&
              date.getFullYear() === currentDate.value.getFullYear()
            );
          })
          .sort((a, b) => {
            const aTime = new Date(a.start?.dateTime || a.start?.date);
            const bTime = new Date(b.start?.dateTime || b.start?.date);
            return aTime - bTime;
          });
      });

      onMounted(() => {
        fetchEventsForMonth();
      });

      return {
        currentMonthEvents,
        formatEventTime,
        getEventMonthShort,
        getEventDayNumber,
      };
    },
  };
</script>

<style scoped>
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #e2e8f0 transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 9999px;
  }
  html.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #1e293b;
  }
</style>
